import { graphql } from 'react-relay';
import { useMemo, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { WatchQueryFetchPolicy } from 'apollo-client';

import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useShareableLinkPageDataProviderShareableLinkPageQuery$data,
	useShareableLinkPageDataProviderShareableLinkPageQuery$variables,
} from './__generated__/useShareableLinkPageDataProviderShareableLinkPageQuery.graphql.ts';
import { PublicLinkPageStatus } from './__types__/ShareableLinkPageTypes.ts';
import type {
	RefetchType,
	ShareableLinkPageDataCalculated,
	ShareableLinkPageProviderData,
	ConfluenceShareableLinkAccessType,
} from './__types__/ShareableLinkPageTypes.ts';
import type { ShareableLinkContentQueryData } from './useShareableLinkContentQuery.ts';
import { useShareableLinkContentQuery } from './useShareableLinkContentQuery.ts';
import type { ShareableLinkPermissionsForObjectQueryData } from './useShareableLinkPermissionsForObject.ts';
import { useShareableLinkPermissionsForObject } from './useShareableLinkPermissionsForObject.ts';

const ENROLLED_CONTENT_TYPES = ['page', 'whiteboard'] as const;

const KNOWN_ERROR_MESSAGES = [
	'This is a Standard/Premium feature. You are currently subscribed to the Free plan.',
	'Anonymous access is not permitted',
];

const i18n = defineMessages({
	shareableLinkUrlPathError: {
		id: 'shareable-links.useShareableLinkPageDataProvider.sharePathError.message.non-final',
		defaultMessage: 'Shareable link status was ON, but no URL path was provided',
		description:
			'Error message when the shareable link status is ON, but no share url is provided.',
	},
});

type ShareableLinkPageData = {
	pageStatus: PublicLinkPageStatus | string;
	publicLinkUrlPath: string | null | undefined;
	accessType: ConfluenceShareableLinkAccessType | string | null | undefined;
};

type ShareableLinkPageQueryData = {
	isLoading: boolean;
	error?: Error;
	data?: ShareableLinkPageData | null | undefined;
	refetch: () => void;
};

type ShareableLinkContentQueryProps =
	// if you want to skip useShareableLinkContentQuery, then a contentType must be provided since it's crucial for computing certain fields
	| {
			skip: true;
			contentType: string;
	  }
	| {
			skip: false;
	  };

/**
 * Computes the full shareable link state from the three query results.
 *
 * Combines page status, content type, and permissions data to determine:
 * - Whether shareable links are admin-enabled, fully enabled, and allowed for the content type
 * - Whether the current user can edit shareable link settings
 * - The access type (INTERNAL_LICENSED, EXTERNAL, or null)
 */
const computeShareableLinkState = ({
	pageData,
	contentData,
	permissionsData,
	isShareableLinkStatusOn,
	enrolledContentTypes,
	edition,
}: {
	pageData: ShareableLinkPageQueryData;
	contentData: ShareableLinkContentQueryData;
	permissionsData: ShareableLinkPermissionsForObjectQueryData;
	isShareableLinkStatusOn: boolean;
	enrolledContentTypes: readonly string[];
	edition: ConfluenceEdition | null;
}): ShareableLinkPageDataCalculated => {
	// Are shareable links allowed for this content type
	const isShareableLinkAllowedForContentType =
		!contentData.error &&
		contentData.data !== undefined &&
		enrolledContentTypes.includes(contentData.data.contentType);

	// Are editable shareable links allowed for this content type
	const isShareableLinkEditAllowedForContentType =
		isShareableLinkAllowedForContentType &&
		edition === ConfluenceEdition.PREMIUM &&
		contentData.data?.contentType === 'whiteboard';

	// Can current user edit shareable link status
	const canEditShareableLink =
		!contentData.error && contentData.data?.canEditShareableLink === true;

	// Does the shareable link have edit permissions (e.g. editable public whiteboards)
	const isEditShareableLink = !!(!permissionsData.error && permissionsData.data?.hasEditPermission);

	// Are shareable links enabled by admins at site and space levels
	const isShareableLinkAdminEnabled =
		isShareableLinkStatusOn ||
		(!pageData.error && pageData.data?.pageStatus === PublicLinkPageStatus.OFF);

	// Is shareable link allowed for content type, admin enabled, and status ON
	const isShareableLinkFullyEnabled =
		isShareableLinkStatusOn && isShareableLinkAdminEnabled && isShareableLinkAllowedForContentType;

	// The access scope of the shareable link
	const accessType = isShareableLinkStatusOn
		? ((pageData.data?.accessType as ConfluenceShareableLinkAccessType | null | undefined) ?? null)
		: null;

	const refetch = (dataToFetch: RefetchType = 'all') => {
		// if dataToFetch !== permission, it means it's either all or public-link
		if (dataToFetch !== 'permission') {
			pageData.refetch();
		}
		// if dataToFetch !== public-link, it means it's either all or permission
		if (dataToFetch !== 'public-link' && isShareableLinkEditAllowedForContentType) {
			permissionsData.refetch();
		}
	};
	return {
		isShareableLinkAdminEnabled,
		isShareableLinkFullyEnabled,
		isShareableLinkAllowedForContentType,
		isShareableLinkEditAllowedForContentType,
		isEditShareableLink,
		canEditShareableLink,
		shareableLinkStatus: pageData.data?.pageStatus as PublicLinkPageStatus | undefined,
		shareableLinkSharePath: pageData.data?.publicLinkUrlPath,
		accessType,
		refetch,
	};
};

/**
 * Orchestrates shareable link data fetching for content.
 *
 * Combines three queries (page status, content type, and permissions) into a single
 * `ShareableLinkPageProviderData` shape consumed by the share-and-restrict dialog.
 * Extends the public-links provider with `accessType` support to distinguish between
 * INTERNAL_LICENSED (workspace) and EXTERNAL (public) links.
 */
export const useShareableLinkPageDataProvider = (props: {
	pageId: string;
	skip?: boolean;
	contentQueryProps?: ShareableLinkContentQueryProps;
	fetchPolicy?: WatchQueryFetchPolicy;
}): ShareableLinkPageProviderData => {
	const intl = useIntl();

	const isLicensed = useIsLicensed();
	const { edition } = useSessionData();

	const contentDataRaw = useShareableLinkContentQuery({
		contentId: props.pageId,
		skip: props.skip || props.contentQueryProps?.skip,
	});

	const contentType = props.contentQueryProps?.skip
		? props.contentQueryProps?.contentType
		: contentDataRaw?.data?.contentType;

	const contentData: ShareableLinkContentQueryData = useMemo(
		() =>
			contentDataRaw.isLoading
				? { isLoading: true }
				: {
						...contentDataRaw,
						data: contentDataRaw.data
							? {
									...contentDataRaw.data,
									contentType: contentType ?? '',
								}
							: {
									canEditShareableLink: false,
									contentType: contentType ?? '',
								},
					},
		[contentDataRaw, contentType],
	);

	const shouldSkip =
		props.skip ||
		!(ENROLLED_CONTENT_TYPES as readonly string[]).includes(contentData?.data?.contentType || '') ||
		!isLicensed ||
		edition === ConfluenceEdition.FREE;

	const shareableLinkPageQueryResult = useQueryInterop<
		useShareableLinkPageDataProviderShareableLinkPageQuery$data,
		useShareableLinkPageDataProviderShareableLinkPageQuery$variables
	>(
		graphql`
			query useShareableLinkPageDataProviderShareableLinkPageQuery($pageId: ID!) {
				publicLinkPage(pageId: $pageId) {
					pageId
					pageStatus
					publicLinkUrlPath
					accessType
				}
			}
		`,
		{
			variables: { pageId: props.pageId },
			fetchPolicy: props.fetchPolicy,
			skip: shouldSkip,
		},
	);

	const permissionsData = useShareableLinkPermissionsForObject({
		objectId: props.pageId,
		skip:
			shouldSkip ||
			edition !== ConfluenceEdition.PREMIUM ||
			contentData?.data?.contentType !== 'whiteboard',
	});

	const pageData: ShareableLinkPageQueryData = useMemo(
		() => ({
			isLoading: shareableLinkPageQueryResult.loading,
			data: shareableLinkPageQueryResult.data?.publicLinkPage,
			error: shareableLinkPageQueryResult.error,
			refetch: shareableLinkPageQueryResult.refetch,
		}),
		[
			shareableLinkPageQueryResult.loading,
			shareableLinkPageQueryResult.data,
			shareableLinkPageQueryResult.error,
			shareableLinkPageQueryResult.refetch,
		],
	);

	useEffect(() => {
		if (
			isUnauthorizedError(shareableLinkPageQueryResult.error) ||
			// We currently aren't being given error types by the backend.
			// When we do, we can better parse these expected errors.
			KNOWN_ERROR_MESSAGES.some((msg) => shareableLinkPageQueryResult.error?.message?.includes(msg))
		) {
			markErrorAsHandled(shareableLinkPageQueryResult.error);
		}
	}, [shareableLinkPageQueryResult.error]);

	// Is shareable link status for page ON
	const isShareableLinkStatusOn =
		!pageData.error && pageData.data?.pageStatus === PublicLinkPageStatus.ON;

	const loading = pageData.isLoading || contentData.isLoading || permissionsData.isLoading;

	const errors = useMemo(() => {
		const queryErrors = [pageData, contentData, permissionsData]
			.map((query) => !query.isLoading && query.error)
			.filter((err): err is Error => Boolean(err));

		if (isShareableLinkStatusOn && pageData.data?.publicLinkUrlPath === null) {
			queryErrors.push(new Error(intl.formatMessage(i18n.shareableLinkUrlPathError)));
		}

		return queryErrors;
	}, [pageData, contentData, permissionsData, isShareableLinkStatusOn, intl]);

	// When the provider is explicitly skipped (e.g. experiment is OFF), return a
	// static empty result immediately. All hooks above still execute (React rules
	// of hooks) but their queries are skipped, so this avoids running the
	// computeShareableLinkState derivation on empty data.
	if (props.skip) {
		return {
			isLoading: false,
			errors: [],
			isShareableLinkAdminEnabled: false,
			isShareableLinkFullyEnabled: false,
			isShareableLinkAllowedForContentType: false,
			isShareableLinkEditAllowedForContentType: false,
			isEditShareableLink: false,
			canEditShareableLink: false,
			shareableLinkStatus: null,
			shareableLinkSharePath: null,
			accessType: null,
			refetch: () => {},
		};
	}

	if (loading)
		return {
			isLoading: true,
			errors,
		};

	return {
		isLoading: false,
		errors,
		...computeShareableLinkState({
			pageData,
			contentData,
			permissionsData,
			isShareableLinkStatusOn,
			enrolledContentTypes: ENROLLED_CONTENT_TYPES,
			edition,
		}),
	};
};
